import { useAccount } from "#contexts/AccountContext"
import { getStandardRetryDelay, getStandardRetryLogic } from "#lib/errors"
import { queryKeys } from "#lib/query-keys"
import { homeFinancialSDK } from "#sdk/sdk"
import type {
  DashboardAnalytics,
  DashboardFilters,
} from "@home-finance-manager/shared/types/dashboard"
import { useQuery } from "@tanstack/react-query"
import * as React from "react"
import { useDebounce } from "./use-debounce"

export function useDashboardAnalytics(filters?: DashboardFilters) {
  const { isReady } = useAccount()
  // Debounce search input to avoid too many requests
  const debouncedSearch = useDebounce(filters?.search || "", 300)

  // Create a stable query key by serializing filters
  const filtersKey = React.useMemo(() => {
    const effectiveFilters = {
      ...filters,
      search: debouncedSearch || undefined,
    }

    if (!effectiveFilters || Object.keys(effectiveFilters).length === 0) {
      return "no-filters"
    }

    // Sort keys to ensure consistent stringification
    const sorted = Object.keys(effectiveFilters)
      .sort()
      .reduce(
        (acc, key) => {
          const value = effectiveFilters[key as keyof DashboardFilters]
          if (value !== undefined && value !== null) {
            // Handle arrays
            if (Array.isArray(value) && value.length > 0) {
              acc[key] = value.sort()
            } else if (!Array.isArray(value)) {
              acc[key] = value
            }
          }
          return acc
        },
        {} as Record<string, any>
      )
    return JSON.stringify(sorted)
  }, [
    filters?.categoryIds,
    filters?.partnerIds,
    debouncedSearch,
    filters?.startDate,
    filters?.endDate,
    filters?.includeVirtualExpenses,
  ])

  // Only enable query if we have both startDate and endDate, or neither
  // If only one is set, user is in the middle of selecting a custom range
  const hasPartialDateRange =
    (filters?.startDate && !filters?.endDate) || (!filters?.startDate && filters?.endDate)

  return useQuery<DashboardAnalytics>({
    queryKey: queryKeys.dashboard.analytics(filtersKey),
    queryFn: () =>
      homeFinancialSDK.accounts.dashboard.getAnalytics({
        ...filters,
        search: debouncedSearch || undefined,
      }),
    enabled: isReady && !hasPartialDateRange,
    refetchOnWindowFocus: false,
    placeholderData: (previousData) => previousData,
    retry: getStandardRetryLogic(),
    retryDelay: getStandardRetryDelay(),
  })
}
